import React, { useEffect, useRef, useState } from 'react'
import { localStoragePayhelm } from 'modules/utils'
import Logo from '../../assets/images/payhelm-logo-blue.png'
import Channels from './Channels'
import Payment from './Payment'
import Invites from './Invites'
import BusinessTypeStep from './AccountType'
import PathChoice from './PathChoice'
import RevenueScale from './RevenueScale'
import ToolsUsed from './ToolsUsed'
import BusinessGoal from './BusinessGoal'
import OnboardingProgress from './OnboardingProgress'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { completeOnboarding, updateOnboardingProfile } from '../../actions/rootActions'
import { selectRootUser, selectRootAccounts } from '../../rootSlice'
import { sendGtmPixel } from 'tagManager'

function asProfile(profile) {
  if (!profile) return {}
  if (typeof profile === 'string') {
    try {
      const parsed = JSON.parse(profile)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  return typeof profile === 'object' ? profile : {}
}

function readStoredStep(serverProfile) {
  const local = parseInt(localStoragePayhelm.signup_step, 10)
  if (Number.isFinite(local)) return local
  const fromServer = parseInt(serverProfile?.step, 10)
  return Number.isFinite(fromServer) ? fromServer : 0
}

// #693: onboarding is a branching wizard. The entry (PathChoice) forks on whether
// the store-analytics suite applies. `store` mirrors the payhelm.com/onboard
// steps; `no_store` drops the store-specific steps and lands the user on /ai.
function SignupMultistep({ loginFromChannel, storeMarketplaceAccountCount }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(selectRootUser)
  const accounts = useSelector(selectRootAccounts)
  const sentOnboardMerchantIds = useRef(new Set())
  const seededOnboardMerchantIds = useRef(false)
  const serverProfile = asProfile(user?.onboarding_profile)
  // Previous run was seeded; account_type was set to 'test' and
  // onboarding_completed flipped false to re-test. Ignore leftover path/answers
  // so PathChoice starts a clean wizard.
  const isRerun = user?.onboarding_completed !== true && !!serverProfile.dashboard_seeded

  const [path, setPath] = useState(() =>
    isRerun ? null : (localStoragePayhelm.onboarding_path || serverProfile.path || null)
  )
  const [step, setStep] = useState(() => (isRerun ? 0 : readStoredStep(serverProfile)))
  const [profile, setProfile] = useState(() => {
    if (isRerun) return {}
    try {
      const local = JSON.parse(localStoragePayhelm.onboarding_answers || 'null')
      if (local && typeof local === 'object') return { ...serverProfile, ...local }
    } catch {
      // fall through to server blob
    }
    return serverProfile
  })

  useEffect(() => {
    if (isRerun) {
      localStoragePayhelm.removeItem?.('signup_step')
      localStoragePayhelm.removeItem?.('onboarding_path')
      localStoragePayhelm.removeItem?.('onboarding_answers')
      localStoragePayhelm.account_type = ''
    }
  }, [isRerun])

  useEffect(() => {
    localStoragePayhelm.signup_step = step
  }, [step])

  // #693: the Intercom launcher is hidden during onboarding (IntercomCurl keys off
  // requires_onboarding), but a proactive message may have already popped open and
  // cover the wizard's Next button — close it on mount.
  useEffect(() => {
    ;(window as any).Intercom?.('hide')
  }, [])

  // Fire the onboard_success pixel once per newly-connected store/marketplace.
  useEffect(() => {
    if (!seededOnboardMerchantIds.current && accounts) {
      accounts
        .filter((a) => a.is_store_marketplace_account && a.merchant_id)
        .forEach((a) => sentOnboardMerchantIds.current.add(a.merchant_id))
      seededOnboardMerchantIds.current = true
      return
    }
    ;(accounts || [])
      .filter((a) => a.is_store_marketplace_account && a.merchant_id)
      .forEach((a) => {
        if (sentOnboardMerchantIds.current.has(a.merchant_id)) return
        sentOnboardMerchantIds.current.add(a.merchant_id)
        sendGtmPixel({ accounts: [a], storeAccount: a }, 'onboard_success')
      })
  }, [accounts])

  const handleSelectPath = (p) => {
    localStoragePayhelm.onboarding_path = p
    localStoragePayhelm.account_type = ''
    const next = { path: p, step: 0 }
    localStoragePayhelm.onboarding_answers = JSON.stringify(next)
    setProfile(next)
    setPath(p)
    setStep(0)
    // Record the path (drives the path-aware landing) but do NOT mark onboarding
    // complete yet — that happens in finish(). reset:true clears a previous run's
    // profile / account_type / seed_* widgets when re-testing the same account.
    dispatch(updateOnboardingProfile({ profile: next, reset: true }))
  }

  const saveAnswer = (partial) => {
    setProfile((prev) => {
      const next = { ...prev, ...partial }
      localStoragePayhelm.onboarding_answers = JSON.stringify(next)
      return next
    })
    dispatch(updateOnboardingProfile({ profile: partial }))
  }

  const saveAccountType = (type) => {
    localStoragePayhelm.account_type = type
    saveAnswer({ businessType: type })
    dispatch(updateOnboardingProfile({ accountType: type }))
  }

  // Entry gate — pick the path before anything else.
  if (!path) {
    return <PathChoice defaultValue={path} onSelect={handleSelectPath} />
  }

  const finish = async () => {
    // Mark onboarding complete durably at the END of the wizard: completeOnboarding
    // POSTs onboarding_completed=true AND flips the client requires_onboarding /
    // onboardSkipped flags. Works whether or not PathChoice ran this session
    // (resume), so a stale client flag can't bounce /dashboard back to /onboard.
    // Server also seeds a hybrid dashboard preset (tabs + widgets) from profile + accounts.
    const data: any = await dispatch(completeOnboarding({ profile: { path } }))
    localStoragePayhelm.removeItem?.('signup_step')
    localStoragePayhelm.removeItem?.('onboarding_path')
    localStoragePayhelm.removeItem?.('onboarding_answers')
    const firstTab = data?.seeded_tab_order?.[0]
    navigate(firstTab ? `/dashboard?view=tab:${encodeURIComponent(firstTab)}` : '/dashboard')
  }
  const storeless = path === 'no_store'

  const stepDefs = storeless
    ? [
        {
          category: 'Business',
          title: 'What best describes your business?',
          text: 'This helps us tailor the platform to your needs.',
          render: (onNext) => (
            <BusinessTypeStep
              initialValue={localStoragePayhelm.account_type || null}
              onBack={onBack}
              onNext={(v) => { saveAccountType(v); onNext() }}
            />
          ),
        },
        {
          category: 'Tools',
          title: 'Which tools do you use?',
          text: 'Select the tools you use — connect them anytime from Integrations. Or skip and jump into the AI assistant.',
          render: (onNext) => (
            <ToolsUsed
              initialValue={profile.tools || []}
              note="You can connect these anytime from Integrations."
              onBack={onBack}
              onSkip={onNext}
              onNext={(v) => { saveAnswer({ tools: v }); onNext() }}
            />
          ),
        },
        {
          category: 'Goal',
          title: 'What would you like to do first?',
          text: "We'll tailor your experience around this.",
          render: (onNext) => (
            <BusinessGoal
              storeless
              initialValue={profile.goal || null}
              onBack={onBack}
              onNext={(v) => { saveAnswer({ goal: v }); onNext() }}
            />
          ),
        },
        {
          category: 'Team',
          title: 'Invite your team',
          text: 'Team members get individual logins with their own permissions. This is optional.',
          render: (onNext) => <Invites onNext={onNext} onBack={onBack} />,
        },
        {
          category: 'Trial',
          title: 'Start your Entrepreneur trial',
          text: 'Start your Entrepreneur 14 day free trial. No commitment — cancel anytime.',
          render: (onNext) => <Payment onNext={onNext} onBack={onBack} />,
        },
      ]
    : [
        {
          category: 'Business',
          title: 'What best describes your business?',
          text: 'This helps us tailor the platform to your business type and reporting needs.',
          render: (onNext) => (
            <BusinessTypeStep
              initialValue={localStoragePayhelm.account_type || null}
              onBack={onBack}
              onNext={(v) => { saveAccountType(v); onNext() }}
            />
          ),
        },
        {
          category: 'Channels',
          title: 'Connect your store',
          text: 'Connect your stores, marketplaces, and payment processors for full analytics. You can add more anytime.',
          render: (onNext) => (
            <Channels
              onNext={onNext}
              onBack={onBack}
              onSkip={onNext}
              loginFromChannel={loginFromChannel}
              storeMarketplaceAccountCount={storeMarketplaceAccountCount}
            />
          ),
        },
        {
          category: 'Revenue',
          title: "What's your revenue scale?",
          text: 'Self-reported revenue helps us give more accurate plan recommendations.',
          render: (onNext) => (
            <RevenueScale
              initialValue={{ revenue: profile.revenue, orders: profile.orders }}
              onBack={onBack}
              onNext={(v) => { saveAnswer(v); onNext() }}
            />
          ),
        },
        {
          category: 'Tools',
          title: 'Which tools do you use?',
          text: "Select the marketing and analytics tools you already use. Skip any that don't apply.",
          render: (onNext) => (
            <ToolsUsed
              initialValue={profile.tools || []}
              onBack={onBack}
              onSkip={onNext}
              onNext={(v) => { saveAnswer({ tools: v }); onNext() }}
            />
          ),
        },
        {
          category: 'Team',
          title: 'Invite your team',
          text: 'Team members get individual logins with their own permissions. This is optional.',
          render: (onNext) => <Invites onNext={onNext} onBack={onBack} />,
        },
        {
          category: 'Goal',
          title: "What's your primary goal?",
          text: "We'll personalise your dashboard and first report around this goal.",
          render: (onNext) => (
            <BusinessGoal
              initialValue={profile.goal || null}
              onBack={onBack}
              onNext={(v) => { saveAnswer({ goal: v }); onNext() }}
            />
          ),
        },
        {
          category: 'Trial',
          title: 'Start your Entrepreneur trial',
          text: 'Start your Entrepreneur 14 day free trial. No commitment — cancel anytime.',
          render: (onNext) => <Payment onNext={onNext} onBack={onBack} />,
        },
      ]

  const totalSteps = stepDefs.length + 1 // +1 for the path-choice entry
  // Clamp so a stale localStorage `signup_step` (e.g. left over from the other
  // path, which has a different length) can never index past the array and blank
  // the wizard. Navigation below realigns `step` to a valid value.
  const safeStep = Math.min(Math.max(step, 0), Math.max(stepDefs.length - 1, 0))
  const current = stepDefs[safeStep]
  if (!current) {
    return null
  }

  const onBack = () => {
    if (safeStep > 0) {
      const next = safeStep - 1
      setStep(next)
      dispatch(updateOnboardingProfile({ profile: { step: next } }))
    } else {
      // Back out to the path choice.
      localStoragePayhelm.onboarding_path = ''
      setPath(null)
      dispatch(updateOnboardingProfile({ profile: { path: null, step: 0 } }))
    }
  }

  const onNext = () => {
    sendGtmPixel({ email: user?.email, step: current.category }, 'onboard_step_finished')
    if (safeStep + 1 >= stepDefs.length) {
      finish()
    } else {
      const next = safeStep + 1
      setStep(next)
      dispatch(updateOnboardingProfile({ profile: { step: next } }))
    }
  }

  return (
    <>
      <div className="tw-fixed tw-inset-0 tw-max-h-screen tw-bg-slate-100 tw-z-[10000] tw-flex tw-flex-col">
        <div className="tw-container tw-mx-auto tw-h-full">
          <div className="tw-grid tw-grid-cols-12 tw-h-full">
            <div className="tw-col-span-12 md:tw-col-span-4 lg:tw-col-span-5 tw-h-fit">
              <div className="tw-px-4 md:tw-px-0 tw-pb-8 md:tw-pb-0">
                <div className="md:tw-pr-12">
                  <div className="tw-my-8 md:tw-my-16">
                    <img src={Logo} className="tw-h-[48px] tw-w-auto" fetchPriority="high" decoding="async" alt="PayHelm" />
                  </div>
                  <div className="tw-mb-5">
                    <OnboardingProgress current={safeStep + 2} total={totalSteps} />
                  </div>
                  <div className="tw-space-y-3">
                    <div className="tw-text-cyan-500 tw-text-sm tw-font-bold">
                      {current.category} · Step {safeStep + 2} of {totalSteps}
                    </div>
                    <div className="tw-text-xl tw-font-bold">{current.title}</div>
                    <div className="tw-text-slate-500">{current.text}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-col-span-12 md:tw-col-span-8 lg:tw-col-span-7 tw-bg-white tw-overflow-y-auto">
              <div>{current.render(onNext)}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SignupMultistep
